import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, Shield, ChartLineUp, Megaphone, X as XIcon, Check } from '@phosphor-icons/react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// IMPORTANT — bumping this version invalidates previously stored consent
// (e.g. when adding new cookie categories or third parties), forcing users
// to re-consent. Mantieni allineato con backend /cookie-consent.
export const COOKIE_CONSENT_VERSION = '2';
const STORAGE_KEY = 'cookie_consent_v2';

/**
 * Reads the current consent. Returns null if not yet given (or stale version).
 */
export function getCookieConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== COOKIE_CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Public helper consumed by the rest of the app to gate analytics calls. */
export function hasAnalyticsConsent() {
  const c = getCookieConsent();
  return !!(c && c.analytics === true);
}

/** Public helper to (re)open the banner from elsewhere (Footer/Settings). */
export function openCookiePreferences() {
  window.dispatchEvent(new CustomEvent('open-cookie-preferences'));
}

const CATEGORIES = [
  {
    key: 'necessary',
    icon: Shield,
    locked: true,
    title: 'Strettamente necessari',
    desc:
      "Senza questi cookie l'app non funziona: ti tengono loggato, mantengono la sessione di pagamento Stripe quando ti abboni, ricordano le tue preferenze di lingua. Non possiamo disattivarli.",
    examples: 'Sessione (auth_token), Stripe checkout, anti-CSRF, accessibilità',
  },
  {
    key: 'analytics',
    icon: ChartLineUp,
    locked: false,
    title: 'Statistiche anonime',
    desc:
      "Usiamo PostHog (servito da PostHog Cloud US) per capire quali funzionalità ti aiutano davvero e quali rivedere. Dati pseudo-anonimi, niente profilazione pubblicitaria. Puoi rifiutare e l'app funziona uguale.",
    examples: 'PostHog (ph_*), durata media sessione, click sulle CTA',
  },
  {
    key: 'marketing',
    icon: Megaphone,
    locked: false,
    title: 'Marketing & promo',
    desc:
      "Per ora non li usiamo (nessun cookie pubblicitario di terze parti). Se in futuro attiveremo retargeting Meta/Google o email marketing avanzato, lo faremo SOLO se hai dato consenso qui.",
    examples: 'Nessuno al momento · placeholder per futuri integrazioni',
  },
];

export default function CookieBanner() {
  const [show, setShow] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const [prefs, setPrefs] = useState({ necessary: true, analytics: false, marketing: false });

  useEffect(() => {
    const existing = getCookieConsent();
    if (!existing) {
      setShow(true);
    }
    const onOpen = () => {
      const cur = getCookieConsent();
      if (cur) {
        setPrefs({ necessary: true, analytics: !!cur.analytics, marketing: !!cur.marketing });
        setShowPanel(true);
      }
      setShow(true);
    };
    window.addEventListener('open-cookie-preferences', onOpen);
    return () => window.removeEventListener('open-cookie-preferences', onOpen);
  }, []);

  const persist = useCallback(async (choice) => {
    const payload = {
      version: COOKIE_CONSENT_VERSION,
      timestamp: new Date().toISOString(),
      necessary: true,
      analytics: !!choice.analytics,
      marketing: !!choice.marketing,
      method: choice.method || 'banner',
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
      /* ignore quota errors — banner won't reappear within the session */
    }
    // Audit log (best effort — non-blocking)
    try {
      axios.post(`${API}/cookie-consent`, payload, { withCredentials: true, timeout: 4000 }).catch(() => {});
    } catch {
      /* ignore */
    }
    // Side effects: load/unload analytics
    if (payload.analytics && typeof window.__loadPosthog === 'function') {
      try { window.__loadPosthog(); } catch { /* ignore */ }
    } else if (!payload.analytics && window.posthog && typeof window.posthog.opt_out_capturing === 'function') {
      try { window.posthog.opt_out_capturing(); } catch { /* ignore */ }
    }
    setShow(false);
    setShowPanel(false);
  }, []);

  const acceptAll = () => persist({ analytics: true, marketing: true, method: 'accept_all' });
  const rejectAll = () => persist({ analytics: false, marketing: false, method: 'reject_all' });
  const saveCustom = () => persist({ ...prefs, method: 'custom' });

  if (!show) {
    // Always render a small "Cookie" pill at the bottom-left to allow reopening prefs
    return <CookieReopenPill onClick={() => setShow(true)} />;
  }

  return (
    <>
      {/* Dimmer behind the banner — does NOT close on click (consent must be explicit) */}
      <div className="fixed inset-0 bg-black/55 backdrop-blur-[2px] z-[60]" data-testid="cookie-overlay" aria-hidden="true" />

      {/* Banner — bloccante. Mobile: full-bottom sheet. Desktop: centered card. */}
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="cookie-banner-title"
        aria-describedby="cookie-banner-desc"
        className="fixed inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center z-[61] p-0 sm:p-4 pointer-events-none"
        data-testid="cookie-banner"
      >
        <div className="bg-white w-full sm:max-w-2xl shadow-2xl border-t-4 border-t-emerald-600 sm:border border-zinc-200 pointer-events-auto max-h-[92vh] flex flex-col">
          {/* Header */}
          <div className="px-5 sm:px-7 py-4 sm:py-5 border-b border-zinc-200 flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-50 border border-emerald-200 flex items-center justify-center flex-shrink-0">
                <Cookie size={20} weight="fill" className="text-emerald-700" />
              </div>
              <div>
                <h2 id="cookie-banner-title" className="text-base sm:text-lg font-black text-zinc-900 tracking-tight">
                  Diamo un'occhiata insieme ai cookie
                </h2>
                <p className="text-[11px] text-zinc-500 mt-0.5">
                  Trasparenza GDPR · revocabile in qualsiasi momento
                </p>
              </div>
            </div>
          </div>

          {/* Intro */}
          {!showPanel && (
            <div className="px-5 sm:px-7 py-4 sm:py-5 overflow-y-auto" id="cookie-banner-desc">
              <p className="text-sm text-zinc-700 leading-relaxed">
                Usiamo i cookie <strong>strettamente necessari</strong> per farti accedere e gestire i tuoi contratti e abbonamenti — questi non si possono disattivare. Per il resto (statistiche anonime, eventuali integrazioni marketing future) <strong>decidi tu</strong>. Puoi accettare tutto, rifiutare tutto o scegliere categoria per categoria.
              </p>
              <p className="text-xs text-zinc-500 leading-relaxed mt-3">
                Maggiori dettagli nella nostra{' '}
                <Link to="/privacy" className="underline hover:text-emerald-700">Privacy Policy</Link>{' e nei '}
                <Link to="/terms" className="underline hover:text-emerald-700">Termini & Condizioni</Link>.
              </p>
            </div>
          )}

          {/* Granular panel */}
          {showPanel && (
            <div className="px-5 sm:px-7 py-4 sm:py-5 overflow-y-auto flex-1">
              <div className="space-y-3">
                {CATEGORIES.map((cat) => {
                  const Icon = cat.icon;
                  const enabled = prefs[cat.key];
                  return (
                    <div key={cat.key} className="border border-zinc-200 p-3.5">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <div className={`w-9 h-9 flex items-center justify-center flex-shrink-0 ${cat.locked ? 'bg-zinc-100 text-zinc-700' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`}>
                            <Icon size={16} weight="bold" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <p className="text-sm font-black text-zinc-900 tracking-tight">{cat.title}</p>
                              {cat.locked && (
                                <span className="text-[9px] font-bold uppercase tracking-wider text-zinc-500 bg-zinc-100 px-1.5 py-0.5">
                                  Sempre attivi
                                </span>
                              )}
                            </div>
                            <p className="text-[12px] text-zinc-600 leading-relaxed">{cat.desc}</p>
                            <p className="text-[10px] text-zinc-400 mt-1.5 italic">Cookie: {cat.examples}</p>
                          </div>
                        </div>
                        {/* Switch */}
                        <button
                          type="button"
                          role="switch"
                          aria-checked={enabled}
                          aria-label={`${cat.title} ${enabled ? 'attivo' : 'disattivo'}`}
                          disabled={cat.locked}
                          onClick={() => !cat.locked && setPrefs((p) => ({ ...p, [cat.key]: !p[cat.key] }))}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1 ${enabled ? 'bg-emerald-600' : 'bg-zinc-300'} ${cat.locked ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                          data-testid={`cookie-toggle-${cat.key}`}
                        >
                          <span className={`inline-block h-4 w-4 bg-white shadow transform transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Actions — GDPR rule: REFUSE button must be as prominent as ACCEPT */}
          <div className="px-5 sm:px-7 py-4 sm:py-4 border-t border-zinc-200 bg-zinc-50">
            {!showPanel ? (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <button
                  onClick={rejectAll}
                  className="h-11 px-4 text-[12px] font-black uppercase tracking-wider bg-white border border-zinc-300 text-zinc-900 hover:border-zinc-900 hover:bg-zinc-50 transition-colors order-2 sm:order-1"
                  data-testid="cookie-reject-btn"
                >
                  Rifiuta tutti
                </button>
                <button
                  onClick={() => setShowPanel(true)}
                  className="h-11 px-4 text-[12px] font-black uppercase tracking-wider bg-white border border-zinc-300 text-zinc-700 hover:border-zinc-900 hover:text-zinc-900 transition-colors order-3 sm:order-2"
                  data-testid="cookie-customize-btn"
                >
                  Personalizza
                </button>
                <button
                  onClick={acceptAll}
                  className="h-11 px-4 text-[12px] font-black uppercase tracking-wider bg-emerald-600 text-white hover:bg-emerald-700 transition-colors order-1 sm:order-3"
                  data-testid="cookie-accept-btn"
                >
                  Accetta tutti
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <button
                  onClick={rejectAll}
                  className="h-11 px-4 text-[12px] font-black uppercase tracking-wider bg-white border border-zinc-300 text-zinc-900 hover:border-zinc-900 hover:bg-zinc-50 transition-colors order-2 sm:order-1"
                  data-testid="cookie-reject-btn-panel"
                >
                  Rifiuta tutti
                </button>
                <button
                  onClick={saveCustom}
                  className="h-11 px-4 text-[12px] font-black uppercase tracking-wider bg-zinc-900 text-white hover:bg-zinc-800 transition-colors order-3 sm:order-2 flex items-center justify-center gap-1.5"
                  data-testid="cookie-save-custom-btn"
                >
                  <Check size={13} weight="bold" /> Salva preferenze
                </button>
                <button
                  onClick={acceptAll}
                  className="h-11 px-4 text-[12px] font-black uppercase tracking-wider bg-emerald-600 text-white hover:bg-emerald-700 transition-colors order-1 sm:order-3"
                  data-testid="cookie-accept-btn-panel"
                >
                  Accetta tutti
                </button>
              </div>
            )}
            <p className="text-[10px] text-zinc-500 text-center mt-3">
              La tua scelta viene registrata con timestamp per conformità GDPR · v{COOKIE_CONSENT_VERSION}
            </p>
          </div>
        </div>
      </div>
    </>
  );
}

function CookieReopenPill({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="hidden md:flex fixed bottom-4 left-4 z-40 items-center gap-1.5 h-8 px-3 bg-white border border-zinc-300 hover:border-zinc-900 hover:bg-zinc-50 transition-colors text-[10px] font-black uppercase tracking-wider text-zinc-700 shadow-sm"
      title="Preferenze cookie"
      data-testid="cookie-reopen-pill"
    >
      <Cookie size={12} weight="fill" className="text-emerald-700" />
      Cookie
    </button>
  );
}
